import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { AuthService } from 'ngx-auth';
import { Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';

import { AuthenticationDataService } from 'app/core/authentication';
import { ChatService } from 'app/core/chat/chat.service';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { IToken } from '../models/token';
import { IUserClaims } from '../models/user-claims';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService implements AuthService {
  private readonly authenticationDataService = inject(AuthenticationDataService);
  private readonly chatService = inject(ChatService);
  private readonly parent = inject(AuthenticationService, { optional: true, skipSelf: true });
  private readonly translationService = inject(TranslationService);

  private interruptedUrl = '';
  private userLoggedOut$ = new Subject<void>();
  private userLoggedIn$ = new ReplaySubject<void>();
  private userSloRedirect$ = new Subject<void>();

  public constructor() {
    if (this.parent) {
      throw Error('Trying to create second instance of AuthenticationService.');
    }
  }

  public isAuthorized(): Observable<boolean> {
    return this.authenticationDataService.isAuthorized();
  }

  public isExpired(): boolean {
    const userClaim = this.getUserClaims();

    if (userClaim === null) {
      return true;
    }

    return DateTime.utc() >= userClaim.expiry;
  }

  public getAccessToken(): Observable<string> {
    return of(this.authenticationDataService.getAccessToken() || '');
  }

  public refreshToken(): Observable<IToken> {
    return throwError(() => new Error('Token refresh is handled by SessionRefreshCoordinatorService.'));
  }

  public getAuthMethod(): string | null | undefined {
    return this.authenticationDataService.getAuthMethod();
  }

  public refreshShouldHappen(_response: HttpErrorResponse): boolean {
    // Session refresh is proactive and activity-gated in SessionRefreshCoordinatorService.
    // Do not use ngx-auth's reactive failed-response refresh flow.
    return false;
  }

  public getInterruptedUrl(): string {
    return this.interruptedUrl;
  }

  public setInterruptedUrl(url: string): void {
    this.interruptedUrl = url;
  }

  public setTokens(tokens: IToken): void {
    this.authenticationDataService
      .setAccessToken(tokens.access_token);

    this.userLoggedIn$.next();
  }

  public logOut(samlRedirect = true): void {
    // Always clear local/webapp state
    this.authenticationDataService.clearUserInfo();
    this.chatService.logOut();
    this.userLoggedOut$.next();

    // Optionally trigger SAML redirect via old Angular
    if (samlRedirect) {
      this.userSloRedirect$.next();

      return;
    }
  }

  public isUserAuthorized(): boolean {
    return this.authenticationDataService.isExistAccessToken();
  }

  public setUserClaims(userClaims: IUserClaims): void {
    this.authenticationDataService.setUserClaims(userClaims);
    this.translationService.switchLanguage(userClaims?.localeId ?? '');
  }

  public skipRequest(request: HttpRequest<any>): boolean {
    // for any http client request that explicity set authorization need to skip ngx-auth interceptor
    // this could function could be extended to replace exclude-header-request.interceptor
    return request.headers.has('Authorization');
  }

  public getUserClaims(): IUserClaims | null {
    return this.authenticationDataService.getUserClaims();
  }

  public getUserLocale(): string {
    return this.authenticationDataService.getUserLocale();
  }

  public userLoggedOut(): Observable<void> {
    return this.userLoggedOut$;
  }

  public userLoggedIn(): Observable<void> {
    return this.userLoggedIn$;
  }

  public userSloRedirect(): Observable<void> {
    return this.userSloRedirect$;
  }

  public roles(): string[] {
    if (!this.isUserAuthorized()) {
      return [];
    }

    return this.getUserClaims()?.roles ?? [];
  }

  public isInRole(role: string): boolean {
    return this.roles().includes(role);
  }

  public isTransient(): boolean {
    if (!this.isUserAuthorized()) {
      return false;
    }

    return this.getUserClaims()?.isTransient ?? false;
  }

  public userId(): string | null {
    if (!this.isUserAuthorized()) {
      return null;
    }

    return this.getUserClaims()?.userId ?? null;
  }
}
